
import { useState } from 'react'
import { MapPin, ChevronDown, Edit3 } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button, ButtonVariant } from '@meiuca/ui-components'
import { useUserLocation } from '@/hooks/useUserLocation'

interface UserLocationSelectorProps {
  onEditLocation?: () => void
  showEditButton?: boolean
  compact?: boolean
}

export function UserLocationSelector({ 
  onEditLocation, 
  showEditButton = true,
  compact = false 
}: UserLocationSelectorProps) {
  const { userLocation, isLoading, getShortLocationName, getFullLocationName, hasLocation } = useUserLocation()
  const [isExpanded, setIsExpanded] = useState(false)

  // Debug log para entender o que está acontecendo
  console.log('🗺️ UserLocationSelector render:', { 
    isLoading, 
    hasLocation, 
    userLocation: userLocation ? {
      placeId: userLocation.placeId,
      mainText: userLocation.mainText,
      hasCoordinate: !!userLocation.coordinate
    } : null,
    compact,
    showEditButton 
  })

  if (isLoading) {
    return (
      <div className="flex items-center space-x-2 text-text-secondary">
        <MapPin className="w-4 h-4 animate-pulse" />
        <span className="text-sm">Carregando localização...</span>
      </div>
    )
  }

  const handleLocationClick = () => {
    if (!hasLocation) {
      onEditLocation?.()
    } else {
      setIsExpanded(!isExpanded)
    }
  }

  const locationText = getShortLocationName(userLocation)
  
  if (compact) {
    return (
      <div className="flex items-center space-x-2">
        <button
          onClick={handleLocationClick}
          className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
            hasLocation 
              ? 'bg-background-secondary hover:bg-background-tertiary text-text-primary' 
              : 'bg-brand-primary/10 hover:bg-brand-primary/20 text-brand-primary'
          }`}
        >
          <MapPin className="w-4 h-4" />
          <span className="text-sm font-medium">{locationText}</span>
          {hasLocation && <ChevronDown className="w-3 h-3" />}
        </button>
        
        {showEditButton && hasLocation && (
          <button
            onClick={onEditLocation}
            className="p-2 rounded-lg bg-background-white border border-border-primary text-text-secondary hover:bg-background-secondary transition-colors"
            title="Editar localização"
          >
            <Edit3 className="w-4 h-4" />
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="relative">
      <button
        onClick={handleLocationClick}
        className={`flex items-center justify-between w-full px-4 py-3 rounded-xl transition-colors ${
          hasLocation 
            ? 'bg-background-white border border-border-primary hover:bg-background-secondary' 
            : 'bg-brand-primary/10 border border-brand-primary/20 hover:bg-brand-primary/20'
        }`}
      >
        <div className="flex items-center space-x-3">
          <MapPin className={`w-5 h-5 ${hasLocation ? 'text-text-secondary' : 'text-brand-primary'}`} />
          <div className="text-left">
            <div className={`font-medium ${hasLocation ? 'text-text-primary' : 'text-brand-primary'}`}>
              {hasLocation ? 'Sua localização' : 'Selecionar localização'}
            </div>
            <div className={`text-sm ${hasLocation ? 'text-text-secondary' : 'text-brand-primary/70'}`}>
              {locationText}
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {showEditButton && hasLocation && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onEditLocation?.()
              }}
              className="p-2 rounded-lg bg-background-secondary hover:bg-background-tertiary transition-colors"
              title="Editar localização"
            >
              <Edit3 className="w-4 h-4 text-text-secondary" />
            </button>
          )}
          {hasLocation && <ChevronDown className="w-4 h-4 text-text-secondary" />}
        </div>
      </button>

      <AnimatePresence>
        {isExpanded && hasLocation && userLocation && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 right-0 mt-2 p-4 bg-background-white rounded-xl border border-border-primary shadow-lg z-50"
          >
            <div className="space-y-2">
              <div className="font-medium text-text-primary">{userLocation.mainText}</div>
              <div className="text-sm text-text-secondary">{userLocation.secondaryText}</div>
              
              {showEditButton && (
                <div className="pt-2 border-t border-border-primary">
                  <Button
                    variant={ButtonVariant.PRIMARY}
                    buttonStyle="clear"
                    size="sm"
                    onClick={onEditLocation}
                    className="w-full"
                  >
                    <Edit3 className="w-4 h-4 mr-2" />
                    Alterar localização
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}